import {
  HHBox,
  HHCard,
  HHCardContent,
  HHChip,
  HHChipProps,
  HHGrid,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { OpenInNew } from '@mui/icons-material';
import { Link } from '@mui/material';
import { ReactNode } from 'react';
import {
  buildTemporalWorkflowUrl,
  LIVE_PROGRESS_SOURCE,
} from '../constants/partner-data-gateway.constant';
import { ChildWorkflowSummary, IngestionDetail } from '../domain/types';
import { printDateAndTime } from '../utils/print-date-and-time';
import { CopyToClipboard } from './copy-to-clipboard';
import { JsonViewer } from './json-viewer';

export const DIAGNOSTICS_TAB_TEST_ID = 'ingestion-diagnostics-tab';
export const DIAGNOSTICS_TEMPORAL_CARD_TEST_ID = 'diagnostics-temporal-card';
export const DIAGNOSTICS_TEMPORAL_WORKFLOW_ID_TEST_ID =
  'diagnostics-temporal-workflow-id';
export const DIAGNOSTICS_TEMPORAL_LINK_TEST_ID = 'diagnostics-temporal-link';
export const DIAGNOSTICS_TEMPORAL_COPY_TEST_ID =
  'diagnostics-temporal-copy-button';
export const DIAGNOSTICS_CHILD_WORKFLOW_COPY_TEST_ID =
  'diagnostics-child-workflow-copy-button';
export const DIAGNOSTICS_LIVE_PROGRESS_SOURCE_TEST_ID =
  'diagnostics-live-progress-source-chip';
export const DIAGNOSTICS_FILE_METADATA_CARD_TEST_ID =
  'diagnostics-file-metadata-card';
export const DIAGNOSTICS_CHILD_WORKFLOW_CARD_TEST_ID =
  'diagnostics-child-workflow-card';
export const DIAGNOSTICS_CHILD_WORKFLOW_STATUS_TEST_ID =
  'diagnostics-child-workflow-status-chip';
export const DIAGNOSTICS_LIVE_PROGRESS_CARD_TEST_ID =
  'diagnostics-live-progress-card';
export const DIAGNOSTICS_STAGE_HISTORY_CARD_TEST_ID =
  'diagnostics-stage-history-card';

export const TEMPORAL_LINK_LABEL = 'Open in Temporal UI';
export const COPY_TEMPORAL_WORKFLOW_ID_LABEL =
  'Copy Temporal workflow ID to clipboard';
export const COPY_CHILD_WORKFLOW_ID_LABEL =
  'Copy child workflow ID to clipboard';

/** Placeholder for a field PDGS left null, so the rows still line up. */
const EMPTY_VALUE = '—';

type ChipColor = NonNullable<HHChipProps['color']>;

/**
 * Badge colour per `liveProgressSource`. PDGS exposes the field as an open
 * string rather than an enum, so a value this build predates falls through to
 * grey — it still renders, just without being flagged as a problem.
 */
export const liveProgressSourceColor = (source: string): ChipColor => {
  switch (source) {
    case LIVE_PROGRESS_SOURCE.TemporalQuery:
      return 'success';
    case LIVE_PROGRESS_SOURCE.Unavailable:
      return 'warning';
    default:
      // Includes `db-final`: a terminal workflow is not a degraded one.
      return 'default';
  }
};

/** Human-readable name for a known source; anything else passes through. */
export const liveProgressSourceLabel = (source: string): string => {
  switch (source) {
    case LIVE_PROGRESS_SOURCE.TemporalQuery:
      return 'Temporal query';
    case LIVE_PROGRESS_SOURCE.DbFinal:
      return 'Database (final)';
    case LIVE_PROGRESS_SOURCE.Unavailable:
      return 'Unavailable';
    default:
      return source;
  }
};

/**
 * Colour per child workflow status. Temporal's terminal states are all
 * failures apart from COMPLETED; anything unrecognised stays neutral.
 */
const childWorkflowStatusColor = (status: string): ChipColor => {
  switch (status) {
    case 'RUNNING':
      return 'info';
    case 'COMPLETED':
      return 'success';
    case 'FAILED':
    case 'TERMINATED':
    case 'TIMED_OUT':
      return 'error';
    default:
      return 'default';
  }
};

interface MetadataRowProps {
  label: string;
  value: string | number | null;
}

/**
 * One `<dt>`/`<dd>` pair. Rendered as bare siblings so every row's label and
 * value line up in the parent `<dl>`'s two grid columns.
 */
const MetadataRow = ({ label, value }: MetadataRowProps): JSX.Element => (
  <>
    <HHBox component="dt">
      <HHTypography hhVariant="body2" color="textSecondary">
        {label}
      </HHTypography>
    </HHBox>
    <HHBox component="dd" margin={0} sx={{ overflowWrap: 'anywhere' }}>
      <HHTypography hhVariant="variant-bypass" variant="body2">
        {value === null || value === '' ? EMPTY_VALUE : value}
      </HHTypography>
    </HHBox>
  </>
);

const MetadataList = ({ children }: { children: ReactNode }): JSX.Element => (
  <HHBox
    component="dl"
    display="grid"
    gridTemplateColumns="max-content minmax(0, 1fr)"
    columnGap={3}
    rowGap={1}
    marginTop={2}
    marginBottom={0}
  >
    {children}
  </HHBox>
);

interface TemporalWorkflowCardProps {
  temporalWorkflowId: string;
  liveProgressSource: string;
}

const TemporalWorkflowCard = ({
  temporalWorkflowId,
  liveProgressSource,
}: TemporalWorkflowCardProps): JSX.Element => (
  <HHCard data-testid={DIAGNOSTICS_TEMPORAL_CARD_TEST_ID}>
    <HHCardContent>
      <HHTypography hhVariant="h3">Temporal Workflow</HHTypography>
      <HHStack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        marginTop={2}
        data-testid={DIAGNOSTICS_TEMPORAL_WORKFLOW_ID_TEST_ID}
      >
        <HHTypography hhVariant="body2" color="textSecondary">
          Workflow ID:
        </HHTypography>
        <HHTypography hhVariant="variant-bypass" variant="body2">
          {temporalWorkflowId}
        </HHTypography>
        <CopyToClipboard
          data={temporalWorkflowId}
          label={COPY_TEMPORAL_WORKFLOW_ID_LABEL}
          testId={DIAGNOSTICS_TEMPORAL_COPY_TEST_ID}
        />
      </HHStack>
      <HHBox marginTop={1}>
        {/* Temporal UI is a separate app, so the link opens in a new tab;
            `noopener` stops it reaching back into this window. */}
        <Link
          href={buildTemporalWorkflowUrl(temporalWorkflowId)}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          data-testid={DIAGNOSTICS_TEMPORAL_LINK_TEST_ID}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          {TEMPORAL_LINK_LABEL}
          <OpenInNew fontSize="inherit" />
        </Link>
      </HHBox>
      <HHStack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        marginTop={2}
      >
        <HHTypography hhVariant="body2" color="textSecondary">
          Live Progress Source:
        </HHTypography>
        <HHChip
          hhVariant="variant-bypass"
          variant="filled"
          size="small"
          color={liveProgressSourceColor(liveProgressSource)}
          label={liveProgressSourceLabel(liveProgressSource)}
          // The page polls every 2 s; announce a change without focus.
          role="status"
          aria-live="polite"
          data-testid={DIAGNOSTICS_LIVE_PROGRESS_SOURCE_TEST_ID}
        />
      </HHStack>
    </HHCardContent>
  </HHCard>
);

const FileMetadataCard = ({
  ingestion,
}: {
  ingestion: IngestionDetail;
}): JSX.Element => (
  <HHCard data-testid={DIAGNOSTICS_FILE_METADATA_CARD_TEST_ID}>
    <HHCardContent>
      <HHTypography hhVariant="h3">File Metadata</HHTypography>
      <MetadataList>
        <MetadataRow label="S3 Key" value={ingestion.s3Key} />
        <MetadataRow label="Bucket" value={ingestion.bucket} />
        <MetadataRow label="ETag" value={ingestion.eTag} />
        <MetadataRow label="File Extension" value={ingestion.fileExtension} />
        <MetadataRow label="Event Type" value={ingestion.eventType} />
        <MetadataRow
          label="Event Time"
          value={
            ingestion.eventTime ? printDateAndTime(ingestion.eventTime) : null
          }
        />
      </MetadataList>
    </HHCardContent>
  </HHCard>
);

const ChildWorkflowCard = ({
  childWorkflow,
}: {
  childWorkflow: ChildWorkflowSummary;
}): JSX.Element => {
  const { progress } = childWorkflow;

  return (
    <HHCard data-testid={DIAGNOSTICS_CHILD_WORKFLOW_CARD_TEST_ID}>
      <HHCardContent>
        <HHStack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
        >
          <HHTypography hhVariant="h3">Child Workflow</HHTypography>
          <HHChip
            hhVariant="variant-bypass"
            variant="filled"
            size="small"
            color={childWorkflowStatusColor(childWorkflow.status)}
            label={childWorkflow.status}
            // Terminal transitions (FAILED, TIMED_OUT) arrive via the poll.
            role="status"
            aria-live="polite"
            data-testid={DIAGNOSTICS_CHILD_WORKFLOW_STATUS_TEST_ID}
          />
        </HHStack>
        <HHStack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          marginTop={2}
        >
          <HHTypography hhVariant="body2" color="textSecondary">
            Workflow ID:
          </HHTypography>
          <HHTypography hhVariant="variant-bypass" variant="body2">
            {childWorkflow.workflowId}
          </HHTypography>
          <CopyToClipboard
            data={childWorkflow.workflowId}
            label={COPY_CHILD_WORKFLOW_ID_LABEL}
            testId={DIAGNOSTICS_CHILD_WORKFLOW_COPY_TEST_ID}
          />
        </HHStack>
        <MetadataList>
          <MetadataRow label="Type" value={childWorkflow.workflowType} />
          <MetadataRow
            label="Started"
            value={printDateAndTime(childWorkflow.startedAt)}
          />
          <MetadataRow
            label="Ended"
            value={
              childWorkflow.endedAt
                ? printDateAndTime(childWorkflow.endedAt)
                : null
            }
          />
          {/* PDGS only populates `progress` while the child workflow is
              RUNNING; it is null once the child finishes. */}
          {progress && (
            <>
              <MetadataRow label="Resolved" value={progress.resolvedCount} />
              <MetadataRow label="Cached" value={progress.cachedCount} />
              <MetadataRow label="Pending" value={progress.pendingCount} />
              <MetadataRow
                label="Outbox Events Emitted"
                value={progress.outboxEventsEmitted}
              />
            </>
          )}
        </MetadataList>
      </HHCardContent>
    </HHCard>
  );
};

interface DiagnosticsTabProps {
  ingestion: IngestionDetail;
}

/**
 * Catch-all operational metadata for debugging a pipeline run. Everything
 * rendered here comes from the `IngestionDetail` the detail page already
 * polls, so the tab adds no further calls to the BFF.
 */
export const DiagnosticsTab = ({
  ingestion,
}: DiagnosticsTabProps): JSX.Element => (
  <HHGrid container spacing={4} data-testid={DIAGNOSTICS_TAB_TEST_ID}>
    <HHGrid item xs={12} md={6}>
      <TemporalWorkflowCard
        temporalWorkflowId={ingestion.temporalWorkflowId}
        liveProgressSource={ingestion.liveProgressSource}
      />
    </HHGrid>
    <HHGrid item xs={12} md={6}>
      <FileMetadataCard ingestion={ingestion} />
    </HHGrid>
    {ingestion.childWorkflow && (
      <HHGrid item xs={12}>
        <ChildWorkflowCard childWorkflow={ingestion.childWorkflow} />
      </HHGrid>
    )}
    {ingestion.liveProgress && (
      <HHGrid item xs={12}>
        <HHCard data-testid={DIAGNOSTICS_LIVE_PROGRESS_CARD_TEST_ID}>
          <HHCardContent>
            <HHTypography hhVariant="h3">Raw Live Progress</HHTypography>
            <JsonViewer
              value={ingestion.liveProgress}
              label="View live progress JSON"
            />
          </HHCardContent>
        </HHCard>
      </HHGrid>
    )}
    {/* Always rendered: the timeline above is a curated view, and a stage
        PDGS added since this build ships is only visible here. */}
    <HHGrid item xs={12}>
      <HHCard data-testid={DIAGNOSTICS_STAGE_HISTORY_CARD_TEST_ID}>
        <HHCardContent>
          <HHTypography hhVariant="h3">Raw Stage History</HHTypography>
          <JsonViewer
            value={ingestion.stageHistory}
            label="View stage history JSON"
          />
        </HHCardContent>
      </HHCard>
    </HHGrid>
  </HHGrid>
);
